/**
 * Message-based noise rules shared by BOTH error sinks: Sentry
 * (`instrumentation-client.ts` beforeSend) and PostHog Error Tracking
 * (`posthog-init.ts` before_send). Matching events are never dropped — they
 * are tagged `signal: 'noise'` + `noise_rule: <id>` so they stay visible for
 * spike detection but can be filtered out of default dashboard views.
 *
 * Rules here match on the exception MESSAGE only, so one definition serves
 * both SDKs' event shapes. Sentry-shape-specific rules (mechanism checks)
 * stay in `instrumentation-client.ts`.
 */
export type MessageNoiseRule = {
  id: string;
  test: (message: string) => boolean;
};

export const MESSAGE_NOISE_RULES: readonly MessageNoiseRule[] = [
  {
    // firebase-js-sdk IndexedDB internals (auth heartbeat/persistence) racing
    // tab close/backgrounding on /sign-in. Known upstream, not actionable:
    // "Database is closing/hidden" and "INTERNAL ASSERTION FAILED: Pending
    // promise was never set".
    id: 'firebase-indexeddb',
    test: (m) =>
      m.includes('Database is closing/hidden') || m.includes('Pending promise was never set'),
  },
  {
    // In-app webview host bridges (TikTok/Instagram iOS injecting scripts that
    // probe window.webkit.messageHandlers; Android WebView tearing down its
    // Java bridge mid-postMessage). Not our code.
    id: 'webview-bridge',
    test: (m) =>
      m.includes('window.webkit.messageHandlers') ||
      m.includes('Java object is gone') ||
      m.includes('Java exception was raised during method invocation'),
  },
  {
    // Facebook/Instagram iOS webviews inject an autofill bridge that calls
    // window._AutofillCallbackHandler / _pcmBridgeCallbackHandler before the
    // host defines them. Not our code, nothing to fix.
    id: 'fb-autofill-bridge',
    test: (m) => m.includes('_AutofillCallbackHandler') || m.includes('_pcmBridgeCallbackHandler'),
  },
  {
    // Crisp chat SDK compiles a named-group regex that Safari < 16.4 rejects
    // ("Invalid regular expression: invalid group specifier name"). Vendor
    // bundle, loads lazyOnload; chat simply doesn't mount on those browsers.
    id: 'crisp-legacy-regex',
    test: (m) => m.includes('invalid group specifier name'),
  },
];

/** First matching rule id for a message, or null. */
export function matchMessageNoiseRule(message: string): string | null {
  return MESSAGE_NOISE_RULES.find((rule) => rule.test(message))?.id ?? null;
}
